"use client";

import { useEffect, useMemo, useState, type MouseEvent } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import {
	Activity,
	ArrowRight,
	BookOpen,
	Calculator,
	Search,
	ShoppingBag,
	SlidersHorizontal,
	X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { LexiconCardModel } from "@/lib/lexicon/card-model";
import type { LexiconEvidenceLevel } from "@/lib/lexicon/types";

const EVIDENCE_LEVELS: LexiconEvidenceLevel[] = [
	"preclinical",
	"clinical-early",
	"clinical-late",
	"clinical-established",
];

const EVIDENCE_KEY: Record<
	LexiconEvidenceLevel,
	"preclinical" | "clinicalEarly" | "clinicalLate" | "clinicalEstablished"
> = {
	preclinical: "preclinical",
	"clinical-early": "clinicalEarly",
	"clinical-late": "clinicalLate",
	"clinical-established": "clinicalEstablished",
};

const EVIDENCE_DOT: Record<LexiconEvidenceLevel, string> = {
	preclinical: "bg-muted-foreground/50",
	"clinical-early": "bg-info-foreground/60",
	"clinical-late": "bg-primary/60",
	"clinical-established": "bg-success",
};

/**
 * Filter chip: >=40px touch target on mobile, compact on desktop pointers.
 * Selected state inverts to foreground-on-background (house pattern).
 */
const chipClass = (selected: boolean) =>
	cn(
		"inline-flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-[12px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background lg:h-8",
		selected
			? "border-foreground bg-foreground text-background"
			: "hover:border-foreground/30 border-border bg-card text-muted-foreground hover:text-foreground",
	);

/**
 * Chip row: horizontally scrollable strip on touch widths (bleeds to the
 * viewport edge so the scroll affordance is visible), wrapping rows on lg.
 */
const CHIP_ROW =
	"-mx-4 flex gap-1.5 overflow-x-auto overscroll-x-contain px-4 py-1 sm:-mx-6 sm:px-6 lg:mx-0 lg:flex-wrap lg:overflow-visible lg:px-0 lg:py-0";

const GROUP_LABEL =
	"mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground lg:mb-0 lg:w-36 lg:shrink-0 lg:pt-1.5";

function searchText(value: string): string {
	return value
		.normalize("NFKD")
		.toLowerCase()
		.replace(/\p{M}/gu, "")
		.replace(/[^\p{L}\p{N}]/gu, "");
}

interface LexiconDirectoryProps {
	entries: LexiconCardModel[];
	/** Research-area slug → localized label (server-localized, shared taxonomy). */
	areaLabels: Record<string, string>;
	locale: string;
	channel: string;
}

/**
 * Interactive lexicon directory. The FULL entry set (~35 substances) is
 * server-rendered into the page — filters and search only hide entries,
 * so SEO sees everything and counts are always the complete truth.
 */
export function LexiconDirectory({ entries, areaLabels, locale, channel }: LexiconDirectoryProps) {
	const t = useTranslations("lexicon");
	const [query, setQuery] = useState("");
	const [areas, setAreas] = useState<ReadonlySet<string>>(new Set());
	const [evidence, setEvidence] = useState<ReadonlySet<string>>(new Set());
	const [onlyProduct, setOnlyProduct] = useState(false);
	const [onlyGuide, setOnlyGuide] = useState(false);
	const [onlyHalfLife, setOnlyHalfLife] = useState(false);
	const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
	const [activeLetter, setActiveLetter] = useState<string | null>(null);

	const filtered = useMemo(() => {
		const q = searchText(query);
		return entries.filter((e) => {
			if (q) {
				const matches = [e.slug, e.name, e.pharmaClass, ...e.aliases].some((value) =>
					searchText(value).includes(q),
				);
				if (!matches) return false;
			}
			if (areas.size > 0 && !e.researchAreas.some((a) => areas.has(a))) return false;
			if (evidence.size > 0 && (!e.evidenceLevel || !evidence.has(e.evidenceLevel))) return false;
			if (onlyProduct && !e.productSlug) return false;
			if (onlyGuide && !e.guideSlug) return false;
			if (onlyHalfLife && !e.halfLifeSlug) return false;
			return true;
		});
	}, [entries, query, areas, evidence, onlyProduct, onlyGuide, onlyHalfLife]);

	const groups = useMemo(() => {
		const map = new Map<string, LexiconCardModel[]>();
		for (const entry of filtered) {
			const list = map.get(entry.letter) ?? [];
			list.push(entry);
			map.set(entry.letter, list);
		}
		return [...map.entries()].sort(([a], [b]) => a.localeCompare(b, locale));
	}, [filtered, locale]);

	const activeLetters = useMemo(() => new Set(groups.map(([letter]) => letter)), [groups]);
	const alphabet = useMemo(
		() =>
			[...new Set(entries.map((entry) => entry.letter))].sort((a, b) =>
				a === "#" ? -1 : b === "#" ? 1 : a.localeCompare(b, locale),
			),
		[entries, locale],
	);
	const availableAreas = useMemo(() => {
		const present = new Set<string>();
		for (const e of entries) for (const a of e.researchAreas) present.add(a);
		return Object.keys(areaLabels).filter((slug) => present.has(slug));
	}, [entries, areaLabels]);

	const activeFilterCount =
		areas.size + evidence.size + (onlyProduct ? 1 : 0) + (onlyGuide ? 1 : 0) + (onlyHalfLife ? 1 : 0);
	const hasActiveState = activeFilterCount > 0 || query.trim() !== "";

	// Active-section highlight for the A-Z register: the first letter group
	// intersecting the band below the sticky bars owns the highlight. A stale
	// activeLetter is harmless — the render gates on activeLetters.has() and
	// the observer fires its initial callback right after observe().
	useEffect(() => {
		if (typeof IntersectionObserver === "undefined") return;
		const sections = groups
			.map(([letter]) => document.getElementById(`lexicon-${letter}`))
			.filter((el): el is HTMLElement => el !== null);
		if (sections.length === 0) return;
		const visibility = new Map<string, boolean>();
		const observer = new IntersectionObserver(
			(observed) => {
				for (const entry of observed) visibility.set(entry.target.id, entry.isIntersecting);
				const current = sections.find((section) => visibility.get(section.id));
				setActiveLetter(current ? current.id.replace("lexicon-", "") : null);
			},
			{ rootMargin: "-120px 0px -55% 0px" },
		);
		for (const section of sections) observer.observe(section);
		return () => observer.disconnect();
	}, [groups]);

	const toggleSetValue = (set: ReadonlySet<string>, value: string): Set<string> => {
		const next = new Set(set);
		if (next.has(value)) next.delete(value);
		else next.add(value);
		return next;
	};

	const clearAll = () => {
		setQuery("");
		setAreas(new Set());
		setEvidence(new Set());
		setOnlyProduct(false);
		setOnlyGuide(false);
		setOnlyHalfLife(false);
	};

	// Real anchors carry the semantics; the click handler only upgrades the
	// jump to a smooth scroll when the user has not asked for reduced motion.
	const jumpToLetter = (event: MouseEvent<HTMLAnchorElement>, letter: string) => {
		const target = document.getElementById(`lexicon-${letter}`);
		if (!target) return;
		event.preventDefault();
		const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		target.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
	};

	const filterPanel = (
		<div className="space-y-4 lg:space-y-2.5">
			{/* Research areas */}
			{availableAreas.length > 0 && (
				<div className="lg:flex lg:items-start lg:gap-4">
					<p className={GROUP_LABEL}>{t("filterAreasLabel")}</p>
					<div className={CHIP_ROW}>
						{availableAreas.map((slug) => {
							const selected = areas.has(slug);
							return (
								<button
									key={slug}
									type="button"
									aria-pressed={selected}
									onClick={() => setAreas(toggleSetValue(areas, slug))}
									className={chipClass(selected)}
								>
									{areaLabels[slug]}
								</button>
							);
						})}
					</div>
				</div>
			)}

			{/* Evidence */}
			<div className="lg:flex lg:items-start lg:gap-4">
				<p className={GROUP_LABEL}>{t("filterEvidenceLabel")}</p>
				<div className={CHIP_ROW}>
					{EVIDENCE_LEVELS.map((level) => {
						const selected = evidence.has(level);
						return (
							<button
								key={level}
								type="button"
								aria-pressed={selected}
								onClick={() => setEvidence(toggleSetValue(evidence, level))}
								className={chipClass(selected)}
							>
								<span className={cn("h-1.5 w-1.5 rounded-full", EVIDENCE_DOT[level])} aria-hidden />
								{t(`evidence.${EVIDENCE_KEY[level]}`)}
							</button>
						);
					})}
				</div>
			</div>

			{/* Availability toggles */}
			<div className="lg:flex lg:items-start lg:gap-4">
				<span aria-hidden className="hidden lg:block lg:w-36 lg:shrink-0" />
				<div className={CHIP_ROW}>
					{(
						[
							[onlyProduct, setOnlyProduct, "filterProduct"],
							[onlyGuide, setOnlyGuide, "filterGuide"],
							[onlyHalfLife, setOnlyHalfLife, "filterHalfLife"],
						] as const
					).map(([value, setter, key]) => (
						<button
							key={key}
							type="button"
							aria-pressed={value}
							onClick={() => setter(!value)}
							className={chipClass(value)}
						>
							{t(key)}
						</button>
					))}
				</div>
			</div>
		</div>
	);

	return (
		<div>
			{/* Controls: search + mobile filter toggle + clear + honest count */}
			<div className="flex flex-wrap items-center gap-2.5">
				<div className="relative w-full sm:w-auto sm:min-w-0 sm:max-w-sm sm:flex-1">
					<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<input
						type="search"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder={t("searchPlaceholder")}
						aria-label={t("searchPlaceholder")}
						className="focus:ring-primary/10 h-11 w-full rounded-lg border border-border bg-card pl-9 pr-10 text-sm shadow-xs placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2"
					/>
					{query && (
						// FOLLOW-UP: aria-label collides with the global clear button below — add a
						// dedicated `lexicon.clearSearch` key across all 21 locales and use it here.
						<button
							type="button"
							onClick={() => setQuery("")}
							aria-label={t("clearFilters")}
							className="absolute right-0.5 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						>
							<X className="h-3.5 w-3.5" />
						</button>
					)}
				</div>

				<button
					type="button"
					onClick={() => setMobileFiltersOpen((v) => !v)}
					aria-expanded={mobileFiltersOpen}
					aria-controls="lexicon-filter-panel"
					className="hover:bg-muted/50 inline-flex h-11 items-center gap-2 rounded-lg border border-border bg-card px-3 text-sm font-medium shadow-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden"
				>
					<SlidersHorizontal className="h-4 w-4" />
					{t("filtersLabel")}
					{activeFilterCount > 0 && (
						<span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-foreground px-1 text-[10px] font-bold tabular-nums text-background">
							{activeFilterCount}
						</span>
					)}
				</button>

				{/* Always-rendered (invisible when idle) so toggling causes no layout shift */}
				<button
					type="button"
					onClick={clearAll}
					tabIndex={hasActiveState ? undefined : -1}
					aria-hidden={hasActiveState ? undefined : true}
					className={cn(
						"inline-flex h-11 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:h-9",
						hasActiveState ? "visible" : "invisible",
					)}
				>
					<X className="h-3.5 w-3.5" aria-hidden />
					{t("clearFilters")}
				</button>

				<span className="ml-auto text-sm tabular-nums text-muted-foreground" aria-live="polite">
					{t("countLabel", { count: filtered.length })}
				</span>
			</div>

			{/* Filter panel — collapsible on mobile so it never blocks the first viewport */}
			<div id="lexicon-filter-panel" className={cn("mt-4", mobileFiltersOpen ? "block" : "hidden lg:block")}>
				{filterPanel}
			</div>

			{/* A-Z register — sticky letter bar, scrollable strip on mobile */}
			<nav
				aria-label={t("azLabel")}
				className="bg-background/95 sticky top-14 z-10 -mx-4 mt-6 overflow-x-auto overscroll-x-contain border-y border-border px-4 py-1.5 backdrop-blur-sm sm:mx-0 sm:rounded-lg sm:border sm:px-2"
			>
				<div className="mx-auto flex w-max gap-0.5">
					{alphabet.map((letter) => {
						if (!activeLetters.has(letter)) {
							return (
								<span
									key={letter}
									aria-hidden
									className="text-muted-foreground/30 flex h-10 w-10 shrink-0 items-center justify-center rounded-md font-mono text-[13px] lg:h-8 lg:w-8"
								>
									{letter}
								</span>
							);
						}
						const isCurrent = activeLetter === letter;
						return (
							<a
								key={letter}
								href={`#lexicon-${letter}`}
								aria-current={isCurrent ? "location" : undefined}
								onClick={(event) => jumpToLetter(event, letter)}
								className={cn(
									"flex h-10 w-10 shrink-0 items-center justify-center rounded-md font-mono text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:h-8 lg:w-8",
									isCurrent ? "bg-foreground text-background" : "text-foreground hover:bg-muted",
								)}
							>
								{letter}
							</a>
						);
					})}
				</div>
			</nav>

			{/* Letter groups */}
			{filtered.length === 0 ? (
				<div className="bg-card/50 mt-10 flex min-h-[280px] flex-col items-center justify-center rounded-xl border border-dashed border-border px-6 py-14 text-center">
					<Search className="text-muted-foreground/40 h-6 w-6" aria-hidden />
					<p className="mt-4 max-w-md text-base text-muted-foreground">{t("noResults")}</p>
					<button
						type="button"
						onClick={clearAll}
						className="hover:border-foreground/40 mt-6 inline-flex h-11 items-center rounded-lg border border-border bg-card px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					>
						{t("clearFilters")}
					</button>
				</div>
			) : (
				<div className="mt-8 space-y-12">
					{groups.map(([letter, items]) => (
						<section key={letter} id={`lexicon-${letter}`} className="scroll-mt-32">
							{/* Encyclopedia register: large muted letter + hairline + honest group count */}
							<div className="mb-5 flex items-center gap-4">
								<h2 className="font-serif text-4xl font-bold leading-none tracking-tight text-muted-foreground sm:text-5xl">
									{letter}
								</h2>
								<span aria-hidden className="h-px flex-1 bg-border" />
								<span className="text-[11px] tabular-nums text-muted-foreground">
									{t("countLabel", { count: items.length })}
								</span>
							</div>
							<ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
								{items.map((entry) => (
									<li key={entry.slug}>
										<LexiconCard entry={entry} areaLabels={areaLabels} locale={locale} channel={channel} />
									</li>
								))}
							</ul>
						</section>
					))}
				</div>
			)}
		</div>
	);
}

function LexiconCard({
	entry,
	areaLabels,
	locale,
	channel,
}: {
	entry: LexiconCardModel;
	areaLabels: Record<string, string>;
	locale: string;
	channel: string;
}) {
	const t = useTranslations("lexicon");
	const base = `/${locale}/${channel}`;

	const areaChips = entry.researchAreas
		.map((slug) => areaLabels[slug])
		.filter((label): label is string => Boolean(label))
		.slice(0, 3);

	const inner = (
		<>
			<div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
				<h3 className="min-w-0 break-words font-serif text-lg font-bold leading-snug tracking-tight transition-colors group-hover:text-primary">
					{entry.name}
				</h3>
				{entry.evidenceLevel && (
					<span className="mt-1 inline-flex shrink-0 items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
						<span className={cn("h-1.5 w-1.5 rounded-full", EVIDENCE_DOT[entry.evidenceLevel])} aria-hidden />
						{t(`evidence.${EVIDENCE_KEY[entry.evidenceLevel]}`)}
					</span>
				)}
			</div>

			<p className="mt-0.5 text-[12px] font-medium text-muted-foreground">{entry.pharmaClass}</p>

			{entry.aliases.length > 0 && (
				<p className="text-muted-foreground/80 mt-1 truncate font-mono text-[11px]">
					{entry.aliases.slice(0, 3).join(" · ")}
				</p>
			)}

			{entry.summary && (
				<p
					lang={entry.summaryLang}
					className="text-foreground/80 mt-2.5 line-clamp-2 text-[13px] leading-relaxed"
				>
					{entry.summary}
				</p>
			)}

			{areaChips.length > 0 && (
				<div className="mt-3 flex flex-wrap gap-1">
					{areaChips.map((label) => (
						<span
							key={label}
							className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
						>
							{label}
						</span>
					))}
				</div>
			)}

			<div className="mt-auto">
				<div className="border-border/60 mt-4 flex items-center gap-3 border-t pt-3 text-muted-foreground">
					{entry.productSlug && (
						<span title={t("status.product")}>
							<ShoppingBag className="h-3.5 w-3.5" aria-hidden />
							<span className="sr-only">{t("status.product")}</span>
						</span>
					)}
					{entry.guideSlug && (
						<span title={t("status.guide")}>
							<BookOpen className="h-3.5 w-3.5" aria-hidden />
							<span className="sr-only">{t("status.guide")}</span>
						</span>
					)}
					<span title={t("status.calculator")}>
						<Calculator className="h-3.5 w-3.5" aria-hidden />
						<span className="sr-only">{t("status.calculator")}</span>
					</span>
					{entry.halfLifeSlug && (
						<span title={t("status.halfLife")} className="inline-flex items-center gap-1">
							<Activity className="h-3.5 w-3.5" aria-hidden />
							<span className="sr-only">{t("status.halfLife")}</span>
						</span>
					)}
					{entry.hasProfile && (
						<span className="ml-auto inline-flex items-center gap-1 text-[12px] font-semibold text-primary">
							{t("profileCta")}
							<ArrowRight
								className="h-3.5 w-3.5 transition-transform motion-safe:group-hover:translate-x-0.5"
								aria-hidden
							/>
						</span>
					)}
				</div>
			</div>
		</>
	);

	if (entry.hasProfile) {
		return (
			<div className="hover:border-primary/40 group flex h-full flex-col rounded-xl border border-border bg-card p-4 transition-[border-color,box-shadow,transform] duration-200 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-safe:hover:-translate-y-0.5 motion-reduce:transition-none sm:p-5">
				<Link
					href={`${base}/peptides/${entry.slug}`}
					className="flex flex-1 flex-col rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
				>
					{inner}
				</Link>
				{entry.guideSlug && (
					<Link
						href={`${base}/guides/${entry.guideSlug}`}
						className="mt-2 inline-flex min-h-10 items-center gap-1.5 text-[12px] font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:min-h-8"
					>
						<BookOpen className="h-3.5 w-3.5" aria-hidden />
						{t("detail.guideLink")}
						<ArrowRight className="h-3 w-3" aria-hidden />
					</Link>
				)}
			</div>
		);
	}

	return (
		<div className="border-border/70 bg-card/60 flex h-full flex-col rounded-xl border p-4 sm:p-5">
			{inner}
			<div className="mt-1 flex flex-wrap gap-x-5 gap-y-0.5 text-[12px]">
				<Link
					href={`${base}/peptide-calculator/${entry.calculatorSlug}`}
					className="inline-flex min-h-10 items-center text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:min-h-8"
				>
					{t("status.calculator")}
				</Link>
				{entry.productSlug && (
					<Link
						href={`${base}/products/${entry.productSlug}`}
						className="inline-flex min-h-10 items-center text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:min-h-8"
					>
						{t("status.product")}
					</Link>
				)}
			</div>
		</div>
	);
}
